import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticleSlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 18网页游戏`;
    }
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [pathname, article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/article" className="text-primary-500 hover:text-primary-600 text-sm">返回文章列表</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.18webgame.com' },
      { '@type': 'ListItem', position: 2, name: '游戏资讯', item: 'https://www.18webgame.com/article' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.18webgame.com/article/${slug}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '18网页游戏',
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main className="pt-16">
        <article className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/article" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">游戏资讯</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>

            <header className="mb-8">
              <span className="inline-block bg-primary-500 text-background-50 text-xs px-3 py-1 rounded-full mb-4 whitespace-nowrap">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> 5 分钟阅读
                </span>
              </div>
            </header>

            <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed [&>h2]:text-xl [&>h2]:md:text-2xl [&>h2]:font-heading [&>h2]:text-foreground-950 [&>h2]:mt-8 [&>h2]:mb-4 [&>p]:mb-4 [&>p]:leading-7 [&>ul]:mb-4 [&>li]:mb-1.5 [&>strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-2">标签:</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 md:p-5 bg-background-100 rounded-xl">
              <Link
                to="/article"
                className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回文章列表
              </Link>
              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5 cursor-pointer hover:text-primary-500 transition-colors duration-200">
                  <i className="ri-heart-line"></i> 收藏
                </span>
                <span className="flex items-center gap-1.5 cursor-pointer hover:text-primary-500 transition-colors duration-200">
                  <i className="ri-share-forward-line"></i> 分享
                </span>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}